/**
 * Member Inbox — per-user email API (TRUS-224)
 *
 * Per-user Outlook/Gmail: connection status, the "needs assignment" triage
 * queue, assigning a synced email to a member, on-demand sync, and send-as-user.
 */

import axios from 'axios';
import { config } from '@/src/config';

const API_URL = config.weaveinApi.baseUrl;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

export interface MailboxStatus {
  connected: boolean;
  provider: string | null;
  email: string | null;
}

export interface NeedsAssignmentEmail {
  id: number;
  user_id: number;
  provider: string;
  provider_message_id: string;
  provider_thread_id: string | null;
  direction: string;
  from_email: string | null;
  from_name: string | null;
  to_emails: string | null;
  cc_emails: string | null;
  subject: string | null;
  snippet: string | null;
  sent_at: string | null;
  is_read: boolean;
}

export interface SendEmailPayload {
  to: string[];
  subject: string;
  body_html: string;
  cc?: string[];
  member_id?: number;
  conversation_id?: number;
  in_reply_to?: string;
  references?: string;
  thread_id?: string;
}


export interface InboxEmail extends NeedsAssignmentEmail {
  archived_at?: string | null;
  deleted_at?: string | null;
  has_attachments?: boolean;
  web_link?: string | null;
  folder?: string;
  internet_message_id?: string | null;
  member_id?: number | null;
  needs_assignment?: boolean;
  assigned_by?: number | null;
  assigned_at?: string | null;
  member_communication_id?: number | null;
}

export interface InboxEmailDetail extends InboxEmail {
  body_text?: string | null;
  body_html?: string | null;
  in_reply_to?: string | null;
  attachments?: unknown;
}

export interface MemberSuggestion {
  member_id: number;
  name: string;
  email: string | null;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
}

export interface EmailTemplate {
  id: number;
  name: string;
  subject?: string | null;
  body?: string | null;
  communication_method?: string;
}

export interface ReplyPayload {
  body_html: string;
  subject?: string;
  cc?: string[];
  /** Uses the org's existing communication templates; {variables} resolve against
   *  the linked member, same as a campaign. */
  template_id?: number;
}

export interface ListEmailParams {
  assigned?: 'all' | 'assigned' | 'unassigned';
  member_id?: number;
  /** Defaults to true server-side: each row holds the body of somebody's mailbox. */
  mine_only?: boolean;
  direction?: 'inbound' | 'outbound';
  folder?: 'inbox' | 'sentitems';
  q?: string;
  /** active hides archived; archived shows only those. Deleted is never listed. */
  view?: 'active' | 'archived' | 'all';
  limit?: number;
  offset?: number;
}

export interface AssignOptions {
  /** A member's thread is one conversation, not N triage items. */
  assign_thread?: boolean;
  /** Teach the auto-linker so the next message from this address links itself. */
  remember_address?: boolean;
}

export const inboxEmailAPI = {
  markRead: async (messageId: number, read = true): Promise<{ success: boolean; is_read: boolean }> => {
    const res = await axios.post(`${API_URL}/api/inbox/email/${messageId}/read`, {}, {
      params: { read }, headers: getAuthHeaders(),
    });
    return res.data;
  },

  /** Put aside without losing it. Does NOT archive anything in the real Outlook mailbox. */
  archive: async (messageId: number, archived = true): Promise<{ success: boolean; archived: boolean }> => {
    const res = await axios.post(`${API_URL}/api/inbox/email/${messageId}/archive`, {}, {
      params: { archived }, headers: getAuthHeaders(),
    });
    return res.data;
  },

  /** Removes it from WeaveIn only — the message stays in Outlook. */
  remove: async (messageId: number): Promise<{ success: boolean }> => {
    const res = await axios.delete(`${API_URL}/api/inbox/email/${messageId}`, { headers: getAuthHeaders() });
    return res.data;
  },

  /** Replies in the original thread from the user's own mailbox. */
  reply: async (
    messageId: number,
    payload: ReplyPayload,
  ): Promise<{ success: boolean; to: string; threaded: boolean; from_email: string }> => {
    const res = await axios.post(`${API_URL}/api/inbox/email/${messageId}/reply`, payload, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  templates: async (): Promise<EmailTemplate[]> => {
    const res = await axios.get(`${API_URL}/api/communication-templates`, {
      params: { communication_method: 'email' }, headers: getAuthHeaders(),
    });
    return Array.isArray(res.data) ? res.data : [];
  },

  /** The single inbox — assigned AND unassigned. `needsAssignment` below only ever
   *  returns the unassigned queue, so an assigned message is otherwise unreachable. */
  list: async (params: ListEmailParams = {}): Promise<{ items: InboxEmail[]; count: number; total: number }> => {
    const res = await axios.get(`${API_URL}/api/inbox/email`, { params, headers: getAuthHeaders() });
    return res.data;
  },

  get: async (messageId: number): Promise<{ message: InboxEmailDetail; thread: InboxEmail[] }> => {
    const res = await axios.get(`${API_URL}/api/inbox/email/${messageId}`, { headers: getAuthHeaders() });
    return res.data;
  },

  suggestions: async (messageId: number): Promise<{ suggestions: MemberSuggestion[] }> => {
    const res = await axios.get(`${API_URL}/api/inbox/email/${messageId}/suggestions`, { headers: getAuthHeaders() });
    return res.data;
  },

  /** Reverse an assignment. Deletes the mirrored member_communications row, so the email
   *  stops appearing in the wrong member's history, and forgets the learned address. */
  unassign: async (messageId: number): Promise<{ success: boolean; previous_member_id: number }> => {
    const res = await axios.post(`${API_URL}/api/inbox/email/${messageId}/unassign`, {}, { headers: getAuthHeaders() });
    return res.data;
  },

  bulkAssign: async (
    items: Array<{ message_id: number; member_id: number }>,
    rememberAddress = true,
  ): Promise<{ success: boolean; assigned: unknown[]; failed: Array<{ message_id: number; error: string }> }> => {
    const res = await axios.post(
      `${API_URL}/api/inbox/email/bulk-assign`,
      { items, remember_address: rememberAddress },
      { headers: getAuthHeaders() },
    );
    return res.data;
  },

  status: async (): Promise<MailboxStatus> => {
    const res = await axios.get(`${API_URL}/api/inbox/email/status`, { headers: getAuthHeaders() });
    return res.data;
  },

  needsAssignment: async (mineOnly = false, limit = 50, offset = 0): Promise<{ items: NeedsAssignmentEmail[]; count: number }> => {
    const res = await axios.get(`${API_URL}/api/inbox/email/needs-assignment`, {
      params: { mine_only: mineOnly, limit, offset },
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  assign: async (
    messageId: number,
    memberId: number,
    options: AssignOptions = {},
  ): Promise<{
    success: boolean;
    member_id: number;
    member_communication_id: number;
    remembered_address: string | null;
    thread_messages_assigned: number;
  }> => {
    const res = await axios.post(
      `${API_URL}/api/inbox/email/${messageId}/assign`,
      { member_id: memberId, ...options },
      { headers: getAuthHeaders() },
    );
    return res.data;
  },

  syncNow: async (): Promise<{ queued: boolean }> => {
    const res = await axios.post(`${API_URL}/api/inbox/email/sync-now`, {}, { headers: getAuthHeaders() });
    return res.data;
  },

  send: async (payload: SendEmailPayload): Promise<{ success: boolean; provider: string; from_email: string; member_communication_id: number | null }> => {
    const res = await axios.post(`${API_URL}/api/inbox/email/send`, payload, { headers: getAuthHeaders() });
    return res.data;
  },
};

export default inboxEmailAPI;
